'use client';

import Link from '@eliadmin/core/src/components/link';
import type {
  ReservationPerson,
  ReservationService,
} from '@eliadmin/core/src/api/reservation/reservation-api';
import type {
  CategoriesEntity,
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { TemplateSettings } from '@eliadmin/core/src/api/general/types/template-settings-types';
import { HeaderLogo } from '@eliadmin/core/src/components/header/header-logo';
import { HeaderMenu } from '@eliadmin/core/src/components/header/header-menu';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MdOutlinePhoneInTalk } from 'react-icons/md';
import { HiAtSymbol } from 'react-icons/hi';
import { useEffect, useState } from 'react';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { HeaderProfileButton } from '@eliadmin/core/src/components/header/header-profile-button';
import MobileSidebar from './mobile-sidebar';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import { HeaderOffers } from './header-offers';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { TourSearchDrawer } from './tour-search-drawer';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { IoMdSettings } from 'react-icons/io';
import { Modal } from '../ui/modal';
import { LiaHandshakeSolid } from 'react-icons/lia';
import { DeferredMeetingForm } from '../reservation/deferred-meeting-form';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getVideovizitkyCopy } from '../videovizitky/copy';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  templateSettings: TemplateSettings['data'];
  categories: {
    data: CategoriesEntity[];
  };
  sidebarMenu: Menu;
  headerMenu: Menu;
  people: ReservationPerson[];
  services: ReservationService[];
};

export function HeaderMain({
  generalLayout,
  headerMenu,
  sidebarMenu,
  categories,
  people,
  services,
}: HeaderMainProps) {
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const videoCopy = getVideovizitkyCopy(locale);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const me = useCustomerStore(s => s.me);

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'bg-background',
          'custom-section',
          'ea-header-main',
          scrolled && 'ea-header-main__scrolled',
        )}
      >
        <div
          className={cn(
            'custom-container--xl',
            'flex-col items-start justify-start gap-8 py-4 sm:py-6 md:flex xl:pb-4 xl:pt-8',
            'ea-header-main__desktop-container',
          )}
        >
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center justify-start gap-4 2xl:gap-8 lg:[&_.ea-header-logo__image]:h-10 2xl:[&_.ea-header-logo__image]:h-12">
              <HeaderLogo generalLayout={generalLayout} />
              <div className="hidden h-12 w-[1px] bg-border md:block md:h-[52px]" />
              <div className="hidden items-center justify-start gap-6 md:flex lg:gap-4 2xl:gap-8">
                <Link
                  prefetch={false}
                  href={`tel:${generalLayout.contact?.phone?.replace(/\s+/g, '')}`}
                  target="_blank"
                  className="flex items-center justify-start gap-2 whitespace-nowrap"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#F6F6F6] md:h-[52px] md:w-[52px]">
                    <MdOutlinePhoneInTalk size={24} className="text-primary" />
                  </div>
                  <div className="flex flex-col items-start justify-start gap-1">
                    <span className="text-sm font-light text-primary lg:text-base">
                      {generalLayout.contact?.phone}
                    </span>
                    <p className="text-xs font-light text-[#969696] lg:text-sm">{t('text800')}</p>
                  </div>
                </Link>
                <Link
                  prefetch={false}
                  href={`mailto:${generalLayout.contact?.email}`}
                  target="_blank"
                  className="flex items-center justify-start gap-2 whitespace-nowrap"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#F6F6F6] md:h-[52px] md:w-[52px]">
                    <HiAtSymbol size={24} className="text-primary" />
                  </div>
                  <div className="flex flex-col items-start justify-start gap-1">
                    <span className="text-sm font-light text-primary lg:text-base">
                      {generalLayout.contact?.email}
                    </span>
                    <p className="text-xs font-light text-[#969696] lg:text-sm">{t('text2008')}</p>
                  </div>
                </Link>
              </div>
            </div>
            <Link
              prefetch={false}
              href={ROUTES.VIDEOCARDS()}
              className="hidden min-h-11 shrink-0 items-center rounded-lg px-1 text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary lg:inline-flex"
            >
              {videoCopy.navigation}
            </Link>
            <div className="hidden shrink-0 items-center justify-end gap-2 xl:flex 2xl:gap-4">
              <Modal
                trigger={
                  <Button className="whitespace-nowrap px-3 2xl:px-4">
                    <LiaHandshakeSolid size={24} /> {t('text3967')}
                  </Button>
                }
                title={t('text3968') ?? ''}
                className={{
                  content:
                    'z-[5000] max-h-[88dvh] overflow-y-scroll rounded-none bg-[#F6F6F6] p-6 sm:max-h-[75dvh] sm:max-w-[1024px] sm:rounded-[16px] md:gap-8 md:rounded-[32px] md:p-8 lg:p-12',
                }}
              >
                <DeferredMeetingForm people={people} services={services} />
              </Modal>
              <SocialIcons
                className={{
                  wrapper:
                    'flex shrink-0 flex-nowrap items-center justify-center gap-2 md:items-start md:justify-start 2xl:gap-4',
                  button:
                    'flex h-[52px] w-[52px] items-center justify-center rounded-[16px] !bg-[#F6F6F6] shadow-none transition-all hover:bg-[#F6F6F6]/90',
                  icon: 'h-5 fill-primary stroke-primary text-primary',
                }}
              />
              {!me?.customer ? <></> : <HeaderProfileButton />}
            </div>
            <div className="flex items-center xl:hidden">
              <MobileSidebar
                menu={sidebarMenu.fields}
                categories={categories.data}
                services={services}
                people={people}
              />
            </div>
          </div>
          <div className="mt-2 w-full xl:hidden">
            <HeaderOffers allCategories={categories.data} mobile />
          </div>
          <div
            data-offers-menu-row
            className="hidden w-full items-center justify-between gap-2 xl:flex"
          >
            <div className="flex items-center gap-2">
              <HeaderOffers allCategories={categories.data} />
              <HeaderMenu menu={headerMenu} categories={categories.data} />
            </div>
            <div className="flex items-center gap-2">
              <Button asChild className="flex items-center justify-center gap-1 pl-2">
                <Link prefetch={false} href="/zajazd-na-mieru">
                  <IoMdSettings size={40} className="min-h-10 min-w-10" />
                  <div className="flex flex-col items-start justify-start gap-1 leading-[12px]">
                    {(() => {
                      const text = t('text3307') ?? '';
                      const words = text.split(' ');
                      const firstLine = words.slice(0, 2).join(' ');
                      const secondLine = words.slice(2).join(' ');

                      return (
                        <>
                          <span>{firstLine}</span>
                          {secondLine && <span>{secondLine}</span>}
                        </>
                      );
                    })()}
                  </div>
                </Link>
              </Button>
              <TourSearchDrawer categories={categories.data} />
            </div>
          </div>
          <div className="mt-2 flex w-full items-center gap-2 xl:hidden">
            <div className="min-w-0 flex-1">
              <Button
                asChild
                className={cn(
                  'flex h-10 w-full items-center !justify-start !gap-1 space-x-0 rounded-[8px] border border-border px-[7px] text-primary transition-colors',
                  'hover:bg-primary hover:text-white',
                )}
              >
                <Link prefetch={false} href="/zajazd-na-mieru">
                  <IoMdSettings className="h-4 w-4 shrink-0 min-[360px]:h-5 min-[360px]:w-5" />
                  <span className="min-w-0 flex-1 whitespace-nowrap text-center text-[11px] min-[360px]:text-xs min-[380px]:text-sm">
                    {locale.startsWith('cs') ? 'Zájezd na míru' : 'Zájazd na mieru'}
                  </span>
                </Link>
              </Button>
            </div>

            <div className="min-w-0 flex-1">
              <TourSearchDrawer categories={categories.data} mobile />
            </div>
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}
